<template>
  <div class="d-flex justify-space-between align-start">
    <div class="tab-content-text d-flex flex-column ga-8">
      <div v-if="props.isEditing && localData.lgpdDefinitiveActive && !localData.lgpdTypeId" class="alert-lgpd-required px-4 py-3 rounded-lg">
        <p class="font-italic">
          <strong>Atenção!</strong> A LGPD definitiva está ativa definitivamente, mas este assistente ainda não possui uma configuração de consentimento.
          Enquanto a configuração não for definida, o assistente apresentará erro durante o atendimento.
        </p>
      </div>
      <div class="d-flex flex-column ga-4">
        <h2 class="title-medium">Verificação de consentimento</h2>
        <p class="body-medium">
          Adicione mensagens de confirmação de consentimento e texto a conversa
          do seu assistente para seguir as leis e regulamento de privacidade.
          <a
            class="body-medium link"
            href="https://rubeus.me/BWuR6nx"
          >
            Saiba mais
          </a>
        </p>

        <div class="d-flex flex-column ga-4">
          <rb-select
            :rules="localData.lgpdDefinitiveActive ? [...props.inputRules.required] : []"
            :required="localData.lgpdDefinitiveActive"
            label="Selecione uma opção"
            inline
            v-model="selectedLgpdType"
            :items="localData.lgpdTypeOptions"
            item-title="title"
            item-value="id"
            clearable
          >
            <template v-slot:item="{ item, props: itemProps }">
              <v-list-item v-bind="itemProps" :title="undefined" class="pa-4">
                <div style="max-width: 768px;">
                  <h2 class="title-medium" style="color: rgb(var(--v-theme-text-neutral-primary));">{{ item.raw.title }}</h2>
                  <p class="body-medium" style="color: rgb(var(--v-theme-text-neutral-primary));">{{ item.raw.description }}</p>
                </div>
              </v-list-item>
            </template>
          </rb-select>
        </div>
      </div>

      <component
        v-if="LGPD_TYPES[selectedLgpdType]?.component"
        :is="LGPD_TYPES[selectedLgpdType].component"
        v-model="localData"
        :input-rules="props.inputRules"
      />
    </div>

    <div v-if="previewMessages.length > 0" class="d-flex flex-column ga-4 preview-container">
      <h2 class="title-medium">Pré-visualização</h2>
      <v-card variant="default" class="pa-4 custom-border-card">
        <div class="preview-messages">
          <template v-for="(msg, index) in previewMessages" :key="index">
            <div v-if="msg.isButtons" class="preview-action-buttons">
              <v-btn
                v-if="msg.rejectLabel"
                variant="outlined"
                color="primary-35"
                rounded="xl"
                class="preview-btn reject label-large"
              >
                {{ msg.rejectLabel }}
              </v-btn>
              <v-btn
                v-if="msg.acceptLabel"
                variant="flat"
                color="primary-35"
                rounded="xl"
                class="preview-btn accept label-large"
              >
                {{ msg.acceptLabel }}
              </v-btn>
            </div>
            <div
              v-else
              :class="['message', msg.isUser ? 'user' : 'assistent']"
            >
              <div class="message-wrapper">
                <v-avatar v-if="!msg.isUser" size="32" :color="assistantImageSrc ? undefined : 'primary-60'">
                  <v-img v-if="assistantImageSrc" :src="assistantImageSrc" class="avatar-image" :alt="assistantDisplayName" />
                  <span v-else class="avatar-initials">{{ getInitials(assistantDisplayName) }}</span>
                </v-avatar>
                <div class="text-content">
                  <div class="message-header">
                    <h3 class="sender-name">{{ msg.isUser ? 'Você' : assistantDisplayName }}</h3>
                    <span class="sended-timestamp">{{ formatDateTime(previewTimestamp) }}</span>
                  </div>
                  <div class="message-text">
                    <span v-html="msg.html"></span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import LgpdLegalBasisAndSignaturesConfig from './lgpd/LgpdLegalBasisAndSignaturesConfig.vue';
import LgpdSignaturesAndLegitimateInterestConsentConfig from './lgpd/LgpdSignaturesAndLegitimateInterestConsentConfig.vue';
import LgpdLegitimateInterestConsentConfig from './lgpd/LgpdLegitimateInterestConsentConfig.vue';
import { formatDateTime, getInitials } from '@/ai_studio/utils/textFormatter';

const LGPD_TYPES = {
  1: {
    key: 'LEGAL_BASIS_AND_SIGNATURES',
    component: LgpdLegalBasisAndSignaturesConfig
  },
  2: {
    key: 'SIGNATURES_AND_LEGITIMATE_INTEREST_CONSENT',
    component: LgpdSignaturesAndLegitimateInterestConsentConfig
  },
  3: {
    key: 'LEGITIMATE_INTEREST_CONSENT',
    component: LgpdLegitimateInterestConsentConfig
  }
};

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  inputRules: {
    type: Object,
    required: true
  },
  assistantName: {
    type: String,
    default: ''
  },
  assistantImage: {
    default: null
  },
  isEditing: {
    type: Boolean,
    default: false
  }
});

const previewTimestamp = new Date();

const localData = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit('update:modelValue', newValue);
  }
});

const selectedLgpdType = computed({
  get: () => localData.value.lgpdTypeId ?? null,
  set: (value) => {
    const newType = LGPD_TYPES[value]?.key;
    const usesLegalBasisSelect = newType === 'LEGAL_BASIS_AND_SIGNATURES';
    if (!usesLegalBasisSelect) {
      localData.value.legalBasisConfig.crmLegalBasisId = null;
    }
    localData.value.lgpdTypeId = value;
  }
});

const selectedLgpdTypeName = computed(() => LGPD_TYPES[selectedLgpdType.value]?.key || null);

const assistantDisplayName = computed(() => props.assistantName || 'Assistente');

const assistantImageSrc = computed(() => {
  const image = props.assistantImage;
  if (typeof image === 'string') return image;
  if (image instanceof File) return URL.createObjectURL(image);
  return null;
});

const hasContent = (html) => !!html?.replace(/<[^>]*>/g, '').trim();

const addMessage = (messages, isUser, html) => {
  if (hasContent(html)) messages.push({ isUser, html });
};

const DEFAULT_ACCEPT_LABEL = 'Aceito';
const DEFAULT_REJECT_LABEL = 'Não aceito';

const addButtons = (messages, acceptLabel, rejectLabel) => {
  messages.push({
    isButtons: true,
    acceptLabel: hasContent(acceptLabel) ? acceptLabel : DEFAULT_ACCEPT_LABEL,
    rejectLabel: hasContent(rejectLabel) ? rejectLabel : DEFAULT_REJECT_LABEL,
  });
};

const previewMessages = computed(() => {
  const messages = [];

  const type = selectedLgpdTypeName.value;
  if (!type) {
    return messages;
  }

  const hasSignatures = (type === 'LEGAL_BASIS_AND_SIGNATURES' || type === 'SIGNATURES_AND_LEGITIMATE_INTEREST_CONSENT');
  if (hasSignatures) {
    for (const signature of localData.value.signaturesConfig) {
      if (!hasContent(signature.acceptanceText)) {
        continue;
      }
      addMessage(messages, false, signature.acceptanceText);
      addButtons(messages, signature.acceptanceKeyword, signature.rejectionKeyword);
      addMessage(messages, true, signature.acceptanceKeyword);
    }
  }

  const legalBasis = localData.value.legalBasisConfig;
  addMessage(messages, false, [legalBasis.privacyText, legalBasis.acceptanceText].filter(Boolean).join('<br>'));

  if (type === 'LEGAL_BASIS_AND_SIGNATURES') {
    addButtons(messages, legalBasis.acceptanceKeyword, legalBasis.rejectionKeyword);
    addMessage(messages, true, legalBasis.acceptanceKeyword);
  }

  return messages;
});
</script>

<style lang='scss' scoped>
@use '@/ai_studio/styles/components/assistents-configuration.scss';

.alert-lgpd-required {
  background-color: #FFB94666;
  color: #00000099;
  font-size: 14px;
  width: 100%;
  font-family: Roboto;
  text-align: left;
}

.preview-container {
  margin-left: 32px;
  width: 615px;
  font-family: Roboto;
  color: rgb(var(--v-theme-neutral-35));
  padding-bottom: 10px;
}

.custom-border-card {
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-bottom: 2px solid rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.preview-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-action-buttons {
  display: flex;
  max-width: 528px;
  gap: 8px;

  .preview-btn {
    flex: 1;

    :deep(.v-btn__content) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }

    &.reject {
      border-color: #C5C7C6;
    }
  }
}

.message {
  border-radius: 8px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: rgb(var(--v-theme-neutral-10));
  padding: 12px;

  .message-wrapper {
    display: flex;
    width: 100%;
    gap: 12px;
  }

  .text-content {
    flex: 1;
    min-width: 0;
  }

  .message-header {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 4px;
    min-width: 0;
  }

  .sender-name {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .sended-timestamp {
    color: rgb(var(--v-theme-neutral-60));
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0.25px;
    flex-shrink: 0;
  }

  .message-text {
    text-align: left;
    word-break: break-word;

    ::v-deep p {
      margin: 0;
    }

    ::v-deep a {
      color: rgb(var(--v-theme-primary-60));
      text-decoration: underline;
    }

    ::v-deep strong {
      font-weight: 600;
    }
  }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-initials {
  font-size: 14px;
  line-height: 20px;
}

.assistent {
  background-color: rgb(var(--v-theme-neutral-95));
  margin-right: auto;
  max-width: 528px;

  .sender-name {
    text-align: left;
  }
}

.user {
  margin-left: auto;
  max-width: 85%;
  background-color: #fff;
  border: 1px solid rgb(var(--v-theme-neutral-80));

  .message-wrapper {
    gap: 0;
  }

  .message-header {
    justify-content: flex-end;
  }

  .sender-name {
    text-align: right;
  }
}
</style>
